import React, { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import { useCurrentWorkspace, useDefaultWorkspace } from '@hooks/workspace';
import { useCurrentStage } from '@hooks/useCurrentStage';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';

import CampaignApprovalHead from './components/CampaignApprovalHead/CampaignApprovalHead';
import CampaignApprovalInfo from './components/CampaignApprovalInfo/CampaignApprovalInfo';
import CampaignApprovalContent from './components/CampaignApprovalContent/CampaignApprovalContent';

type CampaignApprovalProps = {
  approvalId: string;
};

function CampaignApproval({ approvalId }: CampaignApprovalProps) {
  const {
    data: resApprovalWorkflow,
    isLoading: isLoadingApprovalWorkflow,
    error: errorApprovalWorkflow,
    refetch: refetchApprovalWorkflow
  } = useGetApprovalWorkflow({
    params: {
      id: approvalId
    }
  });
  const approvalWorkflow = resApprovalWorkflow?.approvalWorkflow;

  const { currentStage, isPastDueDate } = useCurrentStage({
    stages: approvalWorkflow?.stages || [],
    currentStageId: approvalWorkflow?.currentStageId,
    isFinished: approvalWorkflow?.isFinished,
    isPaused: approvalWorkflow?.isPaused
  });

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.awId,
    async () => {
      await refetchApprovalWorkflow();
    },
    1000,
    (msg) => {
      if (!approvalWorkflow) return false;
      if (msg.payload.awId !== approvalWorkflow.id) return false;
      if (msg.payload.reason !== 'approval-workflow-changed') return false;
      return true;
    },
    'asset-updated'
  );

  if (isLoadingApprovalWorkflow) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 300px)'
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!approvalWorkflow) {
    return <Navigate to="/error-404" />;
  }

  if (errorApprovalWorkflow) {
    return (
      <Navigate
        to={`/campaigns/single/${approvalWorkflow.campaignId}?tab=approvals`}
      />
    );
  }

  return (
    <div className="campaign-approval">
      <CampaignApprovalHead
        approvalWorkflow={approvalWorkflow}
        currentStage={currentStage}
        refetchApprovalWorkflow={refetchApprovalWorkflow}
      />
      <CampaignApprovalInfo
        refetchApprovalWorkflow={refetchApprovalWorkflow}
        approvalWorkflow={approvalWorkflow}
        currentStage={currentStage}
        isPastDueDate={isPastDueDate}
      />
      <CampaignApprovalContent
        approvalWorkflow={approvalWorkflow}
        currentStage={currentStage}
        isPastDueDate={isPastDueDate}
      />
    </div>
  );
}

export default function CampaignApprovalContainer() {
  const { approvalId: approvalIdParam, workspaceId: workspaceIdParam } =
    useParams();

  const defaultWorkspace = useDefaultWorkspace(false);
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const workspaceId =
    workspaceIdParam ?? currentWorkspace?.id ?? defaultWorkspace?.id;
  const needRedirect = !workspaceId || workspaceId !== workspaceIdParam;

  useEffect(() => {
    if (workspaceId) setCurrentWorkspace(workspaceId);
  }, [workspaceId, setCurrentWorkspace]);

  if (needRedirect || !approvalIdParam) {
    return <Navigate to={workspaceId ?? '/dashboard'} replace />;
  }
  return <CampaignApproval approvalId={approvalIdParam} />;
}
